// src/Pages/DashboardAssistant/DashboardChatSidebar.jsx
import React, { useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { DashboardAssistantDataService } from '../../Services/DashboardAssistantDataService';
import DOMPurify from 'dompurify';
import { faArrowUp, faChevronDown, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const OUTPUT_TYPES = [
  { key: 'chart', label: 'Chart' },
  { key: 'table', label: 'Table' },
  { key: 'text', label: 'Text insight' },
];

const DashboardChatSidebar = ({ addTile, collapse }) => {
  const { instance, accounts } = useMsal();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const [outputType, setOutputType] = useState('chart');

  const scrollToBottom = () => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  };

  const getToken = async () => {
    const request = { ...loginRequest, account: accounts[0] };
    const res = await instance.acquireTokenSilent(request);
    return res.accessToken;
  };

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { sender: 'User', html: DOMPurify.sanitize(input) };
    setMessages((prev) => [...prev, userMsg]);
    scrollToBottom();
    setInput('');
    setLoading(true);

    try {
      const token = await getToken();
      const resp = await DashboardAssistantDataService.chat(input, token, {}, undefined, outputType);
      const aiMsg = {
        sender: 'AI',
        html: DOMPurify.sanitize(resp.description || '')
      };
      setMessages((prev) => [...prev, aiMsg]);

      // push a tile
      addTile({
        title: resp?.chartSpec?.title || resp?.title || 'Untitled',
        description: resp?.description || '',
        sqlQuery: resp?.sqlQuery || '',
        chartSpec: resp?.chartSpec,
        data: resp?.data || [],
        columns: resp?.columns || [],
        textContent: resp?.textContent || resp?.description || '',
        tileType: resp?.tileType || outputType || 'chart'
      });
    } catch (err) {
      console.error(err);
      setMessages((prev) => [...prev, { sender: 'System', html: 'An error occurred.' }]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  return (
    <div className="da-chat">
      <div className="da-chat-header">
        <div className="fw-semibold">Assistant chat</div>
        <button className='btn btn-xs btn-outline-dark'>
          <FontAwesomeIcon icon={faChevronDown} onClick={() => collapse()} />
        </button>
      </div>

      <div className="da-chat-messages" ref={listRef}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`msg-row ${m.sender === 'User' ? 'user' : m.sender === 'AI' ? 'ai' : 'sys'}`}
          >
            <div
              className={`msg-bubble ${m.sender === 'User' ? 'user' : m.sender === 'AI' ? 'ai' : 'sys'}`}
              dangerouslySetInnerHTML={{ __html: m.html }}
            />
          </div>
        ))}
      </div>
      <form className="da-chat-input" onSubmit={send}>
        <input
          type='text'
          className="form-control form-control-sm"
          placeholder='e.g., "Show me total sales by region this year"'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <button className="btn btn-accent btn-sm rounded-button ms-1 send-button" type="submit" disabled={loading || !input.trim()}>
          {
          loading ? 
          <FontAwesomeIcon icon={faEllipsis} /> : 
          <FontAwesomeIcon icon={faArrowUp} />
          }
        </button>
      </form>
      <div className='d-flex'>
        <div className="btn-group dropup me-2">
          <button className="btn btn-outline-secondary dropdown-toggle btn-xs" type="button" data-bs-toggle="dropdown" aria-expanded="false">
            {OUTPUT_TYPES.find((t) => t.key === outputType)?.label ?? 'Chart'}
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            {OUTPUT_TYPES.map((opt) => (
              <li key={opt.key}>
                <button
                  type="button"
                  className={`dropdown-item ${outputType === opt.key ? 'active' : ''}`}
                  onClick={() => setOutputType(opt.key)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DashboardChatSidebar;
